import { Button, Typography } from "@mui/material";
import React from "react";
import { CellProps, TableDefinition, TileTable } from "../../../../common/components/TileTable";
import {
  CollectionContext,
  OrganizationDataSourceDto,
} from "../../../../common/service/server/dto/OrganizationDataRecipientDto";

import { Loading } from "../../../components";
import { DataRecipientLogo } from "../../../components/DataRecipientLogo";
import { EmptyInfo } from "../../../components/Empty";
import { usePrimaryOrganizationId } from "../../../hooks";
import { useOrgDataSources } from "../../../hooks/useOrganizationVendors";
import { RouteHelpers } from "../../../root/routes";
import { alphabetically } from "../../../surveys/util";
import { Delete } from "@mui/icons-material";
import { Api } from "../../../../common/service/Api";
import { isBuiltInSourceVendor } from "../../../util/vendors";

const NameCell: React.FC<CellProps<OrganizationDataSourceDto>> = ({ data }) => (
  <span className="d-flex flex-row align-items-center">
    <DataRecipientLogo className="mr-md" dataRecipient={data} size={32} />
    <Typography>{data.name}</Typography>
  </span>
);

const ActionCell: React.FC<CellProps<OrganizationDataSourceDto>> = ({ data }) => (
  <Button href={RouteHelpers.dataMap.dataSource.detail(data.vendorId)} color="secondary">
    View Details
  </Button>
);

const createDeleteCell =
  (
    onDelete: (data: OrganizationDataSourceDto) => Promise<void>,
  ): React.FC<CellProps<OrganizationDataSourceDto>> =>
  ({ data }) =>
    (
      <Button
        onClick={() => onDelete(data)}
        variant="text"
        color="secondary"
        startIcon={<Delete />}
      >
        Remove
      </Button>
    );

const createColumns = (onDelete?: (data: OrganizationDataSourceDto) => Promise<void>) => {
  return [
    {
      heading: null,
      component: NameCell,
    },
    {
      heading: null,
      cellClass: "text-right",
      component: onDelete ? createDeleteCell(onDelete) : ActionCell,
    },
  ];
};

export const DataSourcesTable = ({
  collectionContext,
  allowDeletions,
  hideBuiltins,
}: {
  collectionContext?: CollectionContext[];
  allowDeletions?: boolean;
  hideBuiltins?: boolean;
}) => {
  const orgId = usePrimaryOrganizationId();
  const [dataSources, dataSourceRequest, refresh] = useOrgDataSources(orgId);

  if (dataSourceRequest.running) {
    return <Loading />;
  }

  let filteredDataSources = dataSources;
  if (collectionContext) {
    filteredDataSources = dataSources.filter((src) =>
      collectionContext.some((ctx) => src.collectionContext?.includes(ctx)),
    );
  }

  if (hideBuiltins) {
    filteredDataSources = filteredDataSources.filter((src) => !isBuiltInSourceVendor(src));
  }

  if (!filteredDataSources || filteredDataSources.length < 1) {
    return <EmptyInfo>No data sources</EmptyInfo>;
  }

  const sortedSources = filteredDataSources.sort(alphabetically);

  let handleDelete: (data: OrganizationDataSourceDto) => Promise<void> | undefined;
  if (allowDeletions) {
    handleDelete = async (data) => {
      await Api.orgDataSource.removeOrgDataSource(orgId, data.vendorId);
      await refresh();
    };
  }

  const table: TableDefinition<OrganizationDataSourceDto> = {
    data: sortedSources,
    columns: createColumns(handleDelete),
  };

  return <TileTable variant="filled" table={table} />;
};
